import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DEVICE_MODEL_LABELS } from '@/constants/device';
import type { IDevice } from '@/types/production/device';

export type DeviceRow = IDevice;

interface DeviceTableProps {
  data: DeviceRow[];
  onDelete: (deviceId: string) => void;
  isDeleting?: boolean;
}

export function DeviceTable({ data, onDelete, isDeleting = false }: DeviceTableProps) {
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; device: IDevice | null }>({
    open: false,
    device: null,
  });

  const deviceColumns: ColumnDef<DeviceRow>[] = [
    {
      header: 'Serial',
      accessorKey: 'serial',
      cell: ({ row }) => {
        const device = row.original;
        return device.equipment.serial;
      },
    },
    {
      header: 'Modelo',
      accessorKey: 'product',
      cell: ({ row }) => {
        const device = row.original;
        return DEVICE_MODEL_LABELS[device.model] || device.model;
      },
    },
    {
      header: 'Data de criação',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const device = row.original;
        const date = new Date(device.created_at);
        return date.toLocaleString();
      },
    },
    {
      header: '',
      accessorKey: 'actions',
      cell: ({ row }) => {
        const device = row.original;
        return (
          <td className="flex gap-4 relative whitespace-nowrap pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
            <Link to={`/production/devices/edit?id=${device.id}`}>
              <Button
                variant="ghost"
                size="sm"
                className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                title="Editar"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setDeleteDialog({ open: true, device })}
              className="text-red-600 hover:text-red-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isDeleting}
              title="Excluir equipamento"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </td>
        );
      },
    },
  ];
  const table = useReactTable({
    data,
    columns: deviceColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleConfirmDelete = () => {
    if (deleteDialog.device) {
      onDelete(deleteDialog.device.id);
      setDeleteDialog({ open: false, device: null });
    }
  };

  return (
    <>
      <div className="rounded-md border w-full">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={deviceColumns.length} className="h-24 text-center">
                  Nenhum dado encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, device: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o equipamento{' '}
              <strong>{deleteDialog.device?.equipment.serial}</strong>? Esta ação não pode ser
              desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, device: null })}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-500"
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
